/**
 * Blood-trail tracking. A wounded entity drips a trail (see `sim/blood.ts`);
 * the trail carries a freshness timestamp per tile, so following *increasing*
 * age walks toward whoever is bleeding at its end.
 *
 * An unaware/suspicious guard that SEES blood starts tracking it. Each time it
 * reaches the current trail tile it hill-climbs to the freshest unvisited
 * bloody neighbour — snaking along the trail — until it reaches the bleeder:
 *   - the PLAYER  → engage + broadcast the alarm.
 *   - a wounded GUARD → spend a health pack from inventory and patch it up.
 * A dead-end / no health pack / stuck leg drops the track (and a short cooldown
 * stops it instantly re-noticing the same smear).
 *
 * Movement itself is handled by `updateGuard` (step-locked A* toward
 * `bloodTrack.target`); this module only decides WHERE to head and resolves the
 * end. Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import { applyHealthPack, isWounded } from './body';
import { broadcastCaution, engageGuard } from './awareness';
import type { BloodMap } from './blood';
import type { Guard } from './guard';
import { isBlocked, type MapData } from './map';
import type { Player } from './player';

/** Per-guard state while following a blood trail. Lives on `guard.bloodTrack`. */
export interface BloodTrackState {
  /** The trail tile the guard is currently walking toward. */
  targetX: number;
  targetY: number;
  /** Trail tiles already walked — never re-picked, so the guard doesn't loop. */
  visited: Set<number>;
  /** Ticks left to reach `target` before abandoning this leg as unreachable. */
  legTimeout: number;
}

/** What resolving the trail did this tick — the caller turns it into a log line. */
export type BloodTrackEvent =
  | { kind: 'notice' }
  | { kind: 'foundPlayer' }
  | { kind: 'foundBody'; bodyId: number }
  | { kind: 'healed'; patientId: number; healed: number }
  | { kind: 'lost' }
  | null;

const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1],
];

/** Fresh state for a guard about to start tracking a spotted blood tile. */
function beginTrack(x: number, y: number): BloodTrackState {
  return { targetX: x, targetY: y, visited: new Set(), legTimeout: CONFIG.blood.legTimeoutTicks };
}

function cheb(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

/**
 * Strongest blood tile inside the guard's current view cone, above the notice
 * threshold, excluding its own tile and any blood it has ALREADY investigated
 * (older than `bloodSeenTick`) — so old, dealt-with blood is ignored and only
 * fresh blood draws a new track. null if it sees no significant fresh blood.
 */
function noticeBlood(guard: Guard, blood: BloodMap, map: MapData): { x: number; y: number } | null {
  if (!guard.fovTiles) return null;
  const selfIdx = guard.y * map.width + guard.x;
  let best: { x: number; y: number } | null = null;
  let bestVal = CONFIG.blood.noticeThreshold - 1;
  for (const idx of guard.fovTiles) {
    if (idx === selfIdx) continue;
    if (guard.bloodSeen.has(idx)) continue; // already walked/investigated this smear
    const v = blood.blood[idx] ?? 0;
    if (v <= bestVal) continue;
    if ((blood.age[idx] ?? 0) <= guard.bloodSeenTick) continue; // already investigated
    bestVal = v;
    best = { x: idx % map.width, y: Math.floor(idx / map.width) };
  }
  return best;
}

/**
 * Nearest body (dead player or dead guard) within `reach` Chebyshev tiles — the
 * thing a blood trail leads to. A corpse "explains" the blood: the tracker
 * realises someone was killed here and stops following the smear.
 */
function nearbyCorpse(
  guard: Guard,
  player: Player,
  guards: readonly Guard[],
  reach: number,
): { x: number; y: number; id: number } | null {
  let best: { x: number; y: number; id: number } | null = null;
  let bestD = Infinity;
  if (player.body.dead) {
    // The corpse stays at the death tile even though the ghost roams (player.x/y).
    const cx = player.deathTile?.x ?? player.x;
    const cy = player.deathTile?.y ?? player.y;
    const d = cheb(guard.x, guard.y, cx, cy);
    if (d <= reach) { bestD = d; best = { x: cx, y: cy, id: player.id }; }
  }
  for (const g of guards) {
    if (g.id === guard.id || !g.body.dead || g.stashed) continue; // stashed = out of sight
    const d = cheb(guard.x, guard.y, g.x, g.y);
    if (d <= reach && d < bestD) { bestD = d; best = { x: g.x, y: g.y, id: g.id }; }
  }
  return best;
}

/**
 * Freshest unvisited bloody neighbour of the guard's tile — the next step along
 * the trail. Prefers the most recently blooded tile (age), tie-broken by pooled
 * amount, so the guard heads toward the live bleeder rather than the old splat.
 */
function nextTrailTile(guard: Guard, blood: BloodMap, map: MapData, visited: Set<number>): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestAge = -1;
  let bestAmt = -1;
  for (const [dx, dy] of NEIGHBORS) {
    const nx = guard.x + dx;
    const ny = guard.y + dy;
    const idx = ny * map.width + nx;
    if (visited.has(idx)) continue;
    if (isBlocked(map, nx, ny)) continue;
    const amt = blood.blood[idx] ?? 0;
    if (amt < CONFIG.blood.minTrail) continue;
    const age = blood.age[idx] ?? 0;
    if (age > bestAge || (age === bestAge && amt > bestAmt)) {
      bestAge = age;
      bestAmt = amt;
      best = { x: nx, y: ny };
    }
  }
  return best;
}

/** Nearest live, wounded OTHER guard within `reach` Chebyshev tiles. */
function nearbyWoundedGuard(guard: Guard, guards: readonly Guard[], reach: number): Guard | null {
  let best: Guard | null = null;
  let bestD = Infinity;
  for (const g of guards) {
    if (g.id === guard.id || g.body.dead) continue;
    if (!isWounded(g.body)) continue;
    const d = cheb(guard.x, guard.y, g.x, g.y);
    if (d <= reach && d < bestD) { bestD = d; best = g; }
  }
  return best;
}

/**
 * Advance one guard's blood-tracking behaviour for this tick. Run AFTER
 * awareness + broadcast (so a guard that actually sees the player, or is on
 * power-restore duty, drops tracking) and BEFORE `updateGuard` (which moves the
 * guard toward `bloodTrack.target`). Returns an event for the caller to log, or
 * null when nothing notable happened.
 */
export function updateGuardBloodTracking(
  guard: Guard,
  player: Player,
  guards: readonly Guard[],
  blood: BloodMap,
  map: MapData,
): BloodTrackEvent {
  if (guard.body.dead) return null;
  // Real threat or a standing order outranks a blood trail — abandon it.
  if (guard.state === 'alert' || guard.restorePower) {
    guard.bloodTrack = null;
    return null;
  }
  // A bleeding guard is the casualty at the end of a trail, not a tracker.
  if (isWounded(guard.body)) {
    guard.bloodTrack = null;
    return null;
  }
  // Only decide on tile boundaries; mid-step, let the move finish.
  if (guard.moveProgress !== 0) return null;

  const reach = CONFIG.blood.findReachTiles;

  if (!guard.bloodTrack) {
    if (guard.bloodCooldown > 0) { guard.bloodCooldown -= 1; return null; }
    const spot = noticeBlood(guard, blood, map);
    if (!spot) return null;
    // Following blood is NEUTRAL — the guard is curious, not yet suspicious of an
    // intruder. Its alertness only changes based on what it finds at the end.
    guard.bloodTrack = beginTrack(spot.x, spot.y);
    guard.path = null;
    return { kind: 'notice' };
  }

  const track = guard.bloodTrack;

  // Resolve the bleeder as soon as we're within reach — checked every settled
  // tick, not just on exact arrival. The freshest trail tile is often the
  // bleeder's OWN (occupied) tile, which the guard can't step onto; reach-based
  // resolution lets it act from the adjacent tile instead of stalling there.
  if (!player.body.dead && cheb(guard.x, guard.y, player.x, player.y) <= reach) {
    // Found an ENEMY at the end → engage + raise the section (full alert).
    engageGuard(guard, player);
    broadcastCaution(guard, guards, CONFIG.awareness.broadcastRadius);
    endTrack(guard, blood, 'foundPlayer');
    return { kind: 'foundPlayer' };
  }
  const patient = nearbyWoundedGuard(guard, guards, reach);
  if (patient) {
    if (guard.healthPacks > 0) {
      guard.healthPacks -= 1;
      const healed = applyHealthPack(patient.body, CONFIG.blood.healthPackHpPerLimb);
      // Found a wounded MATE → patch them up AND go cautious: someone did this,
      // so search the area rather than shrug it off.
      const cautious = CONFIG.awareness.thresholds.cautious;
      if (guard.awareness < cautious) guard.awareness = cautious;
      if (guard.state === 'unaware' || guard.state === 'suspicious') guard.state = 'cautious';
      guard.investigationX = patient.x;
      guard.investigationY = patient.y;
      endTrack(guard, blood, 'healed');
      return { kind: 'healed', patientId: patient.id, healed };
    }
    // Found the casualty but has nothing to give — stand down.
    return endTrack(guard, blood, 'lost');
  }

  // A BODY at the end of the trail explains the blood — the tracker realises
  // someone was killed here (the player's own corpse counts). Go cautious and
  // search the spot, then stop tracking so it never loops back onto the smear.
  const corpse = nearbyCorpse(guard, player, guards, reach);
  if (corpse) {
    const cautious = CONFIG.awareness.thresholds.cautious;
    if (guard.awareness < cautious) guard.awareness = cautious;
    if (guard.state === 'unaware' || guard.state === 'suspicious') guard.state = 'cautious';
    guard.investigationX = corpse.x;
    guard.investigationY = corpse.y;
    broadcastCaution(guard, guards, CONFIG.awareness.broadcastRadius);
    endTrack(guard, blood, 'lost');
    return { kind: 'foundBody', bodyId: corpse.id };
  }

  // Nobody in reach yet → keep walking to the current trail tile.
  if (guard.x !== track.targetX || guard.y !== track.targetY) {
    track.legTimeout -= 1;
    if (track.legTimeout <= 0) return endTrack(guard, blood, 'lost');
    return null;
  }

  // Arrived at a trail tile with nobody in reach — record it (so we never
  // double back, this tick OR in a future track) and advance to the freshest
  // unvisited bloody neighbour.
  const hereIdx = guard.y * map.width + guard.x;
  track.visited.add(hereIdx);
  guard.bloodSeen.add(hereIdx);
  track.legTimeout = CONFIG.blood.legTimeoutTicks;
  const next = nextTrailTile(guard, blood, map, track.visited);
  if (!next) return endTrack(guard, blood, 'lost');
  track.targetX = next.x;
  track.targetY = next.y;
  guard.path = null;
  return null;
}

/** Clear the track, arm the re-notice cooldown, and mark this trail's blood as
 *  investigated (so the guard won't re-track the same smear again). Records both
 *  the tiles it walked AND any blood still in view, then advances the freshness
 *  cutoff. Returns the matching event only for 'lost' (resolved cases return
 *  their richer event). */
function endTrack(guard: Guard, blood: BloodMap, why: 'lost' | 'foundPlayer' | 'healed'): BloodTrackEvent {
  // Fold the walked trail + all visible blood into the per-tile "seen" set so a
  // guard that tracked once and found nothing (or a body) won't re-lock it.
  if (guard.bloodTrack) for (const idx of guard.bloodTrack.visited) guard.bloodSeen.add(idx);
  if (guard.fovTiles) {
    for (const idx of guard.fovTiles) {
      if ((blood.blood[idx] ?? 0) >= CONFIG.blood.minTrail) guard.bloodSeen.add(idx);
    }
  }
  guard.bloodTrack = null;
  guard.bloodCooldown = CONFIG.blood.trackCooldownTicks;
  guard.bloodSeenTick = blood.tick; // everything bled so far is now "old news"
  return why === 'lost' ? { kind: 'lost' } : null;
}
